/**
 * What the owner attention panel says, decided away from the drawing.
 *
 * Pure kind in, pure look out: the words the panel leads with and the color
 * family it wears, for one moment where Brunel needs its owner. Kept here so
 * every state can be tested without a browser, and so the component stays
 * about drawing and callbacks.
 *
 * The kinds are the planner's own vocabulary, not a second lifecycle: approval
 * matches the plan step that waits for him, a proposed next step matches the
 * next instruction the planner proposed, and a failure is the planner's own
 * failed outcome. Nothing here runs anything - the panel asks, the owner
 * decides, the parent carries the action out.
 */
import type { PlanStep } from './planned-run';

export type OwnerAttentionKind =
  | 'information_required'
  | 'worker_waiting_for_input'
  | 'approval_required'
  | 'planner_proposed_next'
  | 'failed_intervention_required'
  | 'warning'
  | 'owner_takeover_available'
  | 'resolved';

export type OwnerAttentionTone = 'input' | 'approval' | 'failure' | 'info' | 'resolved';

export interface OwnerAttentionLook {
  /** Stable key for tests and data attributes; never a class on its own. */
  readonly key: string;
  /** The words the panel leads with, above the owner's own reason. */
  readonly heading: string;
  /** Color family, carried as a modifier class, never inline. */
  readonly tone: OwnerAttentionTone;
  /** True when something has already gone wrong and the owner must act. */
  readonly serious: boolean;
}

const LOOKS: Readonly<Record<OwnerAttentionKind, OwnerAttentionLook>> = {
  information_required: {
    key: 'information_required', heading: 'Brunel needs information from you', tone: 'info', serious: false,
  },
  worker_waiting_for_input: {
    key: 'worker_waiting_for_input', heading: 'A Worker is waiting for you to type', tone: 'input', serious: false,
  },
  approval_required: {
    key: 'approval_required', heading: 'Approval required before anything is sent', tone: 'approval', serious: false,
  },
  planner_proposed_next: {
    key: 'planner_proposed_next', heading: 'The planner proposes a next step', tone: 'approval', serious: false,
  },
  failed_intervention_required: {
    key: 'failed_intervention_required', heading: 'Failed - the owner must step in', tone: 'failure', serious: true,
  },
  warning: {
    key: 'warning', heading: 'Worth your attention', tone: 'input', serious: false,
  },
  owner_takeover_available: {
    key: 'owner_takeover_available', heading: 'You can take the keyboard now', tone: 'info', serious: false,
  },
  resolved: {
    key: 'resolved', heading: 'Resolved - nothing is waiting on you', tone: 'resolved', serious: false,
  },
};

/** What a kind the caller does not name falls back to: calm, honest, still useful. */
export const UNKNOWN_ATTENTION_LOOK: OwnerAttentionLook = {
  key: 'unknown', heading: 'Brunel needs your attention', tone: 'info', serious: false,
};

/** One attention kind in, one truthful look out. Never a guess that flatters. */
export function ownerAttentionLook(kind: string): OwnerAttentionLook {
  return LOOKS[kind as OwnerAttentionKind] ?? UNKNOWN_ATTENTION_LOOK;
}

/**
 * The words on the approve button. A consequential proposal is never hidden
 * behind `OK`: the button says what approving will cause.
 */
export function approveLabel(hasProposal: boolean): string {
  return hasProposal ? 'Approve next Worker instruction' : 'Approve';
}

/**
 * What approving sends: the owner's edit when he wrote one, the proposal when
 * he did not, and nothing when there was never an instruction. Editing alone
 * sends nothing; this is only ever called when the owner presses approve.
 */
export function approvePayload(
  proposedInstruction: string | null,
  draft: string | null,
): string | null {
  if (proposedInstruction === null) return null;
  const edited = draft?.trim() ?? '';
  return edited.length > 0 ? edited : proposedInstruction;
}

/**
 * A plan step the planner produced, read as the attention kind this panel
 * draws. Steps the owner never needs to see return null, so the caller can
 * skip the panel entirely rather than showing an empty box.
 */
export function attentionKindForStep(step: PlanStep): OwnerAttentionKind | null {
  switch (step.kind) {
    case 'needs_approval': return 'approval_required';
    case 'finished':
      if (step.outcome === 'failed') return 'failed_intervention_required';
      if (step.outcome === 'needs_owner') return 'planner_proposed_next';
      if (step.outcome === 'completed') return 'resolved';
      return null;
    case 'wait': return 'information_required';
    case 'send':
    case 'observe':
      return null;
  }
}

/**
 * Whether this panel actually has a proposal to approve: an action or an
 * instruction with meaningful text. An empty action must not mask a real
 * instruction, and whitespace alone is not a proposal.
 */
export function hasMeaningfulProposal(
  proposedAction: string | null | undefined,
  proposedInstruction: string | null | undefined,
): boolean {
  return (proposedAction?.trim().length ?? 0) > 0 || (proposedInstruction?.trim().length ?? 0) > 0;
}

/**
 * Whether an editable instruction is actually on the table. Edit is offered
 * only when there is something to edit - an omitted instruction is not one.
 */
export function hasEditableInstruction(
  proposedInstruction: string | null | undefined,
): boolean {
  return (proposedInstruction?.trim().length ?? 0) > 0;
}
